// Sitewide CSP-safe broken-image fallback (MN-9278). `error` events on <img>
// don't bubble, so delegation must use the capture phase; entries call
// initImageErrorFallback() at module scope because images in the initial HTML
// can fail before DOMContentLoaded. Images that failed before this bundle
// executed never re-fire `error`, so sweepBrokenImages catches them by
// inspecting `complete && naturalWidth === 0` at init and again at window
// load. Entries MUST use a named import + explicit call — a bare side-effect
// import (`import "common/jiffy/image-error-fallback"`) is dropped by the
// production build's tree-shaking (optimization.sideEffects analysis), which
// leaves the module in the bundle but never executes it.
// Same 1x1 transparent GIF as ApplicationHelper#one_pixel_image
// (packages/core/app/helpers/application_helper.rb).
export const FALLBACK_IMAGE_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const FAILED_CLASS = "img-load-failed";

const isInlineSrc = (src: string): boolean => src.startsWith("data:") || src.startsWith("blob:");

const isSvgSrc = (src: string): boolean =>
  (src.split(/[?#]/)[0] ?? "").toLowerCase().endsWith(".svg");

const applyFallback = (img: HTMLImageElement): void => {
  // currentSrc is the actual failing candidate (srcset-aware); the src
  // attribute is the baseline. Guard on it so lazysizes' data:/blob:
  // placeholders are never treated as broken — and so the fallback swap
  // itself (a data: URI that cannot network-fail) can't loop.
  const src = img.currentSrc || img.getAttribute("src") || "";

  if (!src || isInlineSrc(src)) return;
  if (img.hasAttribute("data-no-fallback")) return;

  // A matching sibling <source> would override the swapped src, so drop the
  // sources when the image sits inside a <picture>.
  if (img.parentElement instanceof HTMLPictureElement) {
    img.parentElement.querySelectorAll("source").forEach(source => source.remove());
  }

  // Marker for styling/QA; preserve the failing URL for error-telemetry
  // consumers (e.g. product-mosaic) that read the src after the swap.
  img.setAttribute("data-fallback-applied", "1");
  img.setAttribute("data-original-src", src);
  img.removeAttribute("srcset");
  img.removeAttribute("sizes");
  img.classList.add(FAILED_CLASS);
  // eslint-disable-next-line no-console -- breadcrumb so real breakage stays visible to engineers
  console.debug("[image-error-fallback] replaced broken image:", src);
  img.setAttribute("src", FALLBACK_IMAGE_SRC);
};

export const onImageError = (event: Event): void => {
  if (!(event.target instanceof HTMLImageElement)) return;

  applyFallback(event.target);
};

// Self-heals a recycled element that failed once and later loads a working
// src: without this, `img-load-failed` / data markers stick forever. The
// placeholder swap itself fires `load` (data: URI), so only a src other than
// the placeholder counts as a recovery.
export const onImageLoad = (event: Event): void => {
  if (!(event.target instanceof HTMLImageElement)) return;

  const img = event.target;

  if (!img.hasAttribute("data-fallback-applied")) return;

  const src = img.currentSrc || img.getAttribute("src") || "";

  if (src === FALLBACK_IMAGE_SRC) {
    // Swallow the placeholder's own load so application load handlers
    // (e.g. DTF PDP artwork telemetry) don't record a failed image as a
    // successful render. Capture phase runs before target-phase handlers.
    event.stopPropagation();
    return;
  }

  img.removeAttribute("data-fallback-applied");
  img.removeAttribute("data-original-src");
  img.classList.remove(FAILED_CLASS);
};

// Catches images whose error event fired before this bundle executed. SVGs
// are skipped: engines with the SVG intrinsic-size bug (Firefox < ~133, some
// WebKit versions) report naturalWidth === 0 for successfully loaded SVGs
// without width/height attributes, and unlike the error-event path the sweep
// has no signal that a failure actually occurred.
export const sweepBrokenImages = (doc: Document = document): void => {
  doc.querySelectorAll("img:not([data-fallback-applied])").forEach(img => {
    if (!(img instanceof HTMLImageElement)) return;
    if (!img.complete || img.naturalWidth !== 0) return;

    const src = img.currentSrc || img.getAttribute("src") || "";

    if (isSvgSrc(src)) return;

    applyFallback(img);
  });
};

export const initImageErrorFallback = (doc: Document = document): void => {
  // Idempotency guard: the module is imported by several entrypoints
  // (base.js, react.ts, design-tag-react.ts). No page loads two of them
  // today, but if one ever does under separate bundler runtimes the
  // listener pair would register twice and the sweep would run twice.
  const view = doc.defaultView as (Window & { __imgErrFallbackInit?: boolean }) | null;

  if (view?.__imgErrFallbackInit) return;
  if (view) view.__imgErrFallbackInit = true;

  doc.addEventListener("error", onImageError, true);
  doc.addEventListener("load", onImageLoad, true);
  sweepBrokenImages(doc);
  doc.defaultView?.addEventListener("load", () => sweepBrokenImages(doc));
};
